import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import { Button } from "@/components/core/buttons/button";
import { Form, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input";
import { InputFile } from "@/components/core/forms/input-file";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import Tooltip from "@/components/core/tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import { useEvidenceMutation } from "../api/queries/evidence-query";
import { type CaseEvidence, type CaseEvidenceForm, caseEvidenceFormSchema } from "../types/evidence";

export type EvidenceRequirements = {
	case_id: string;
	parties: string;
	evidence_type: string;
	case_record_user_id?: string;
};

export default function CaseEvidenceEditModal({
	data,
	submarking,
	newSubmarking,
	selectedText,
	url,
	file,
	requiredEvidence,
	onSubmit,
	amend,
	isDrawing,
	handleSave,
}: {
	data: CaseEvidence;
	submarking?: CaseEvidence;
	newSubmarking?: CaseEvidence;
	selectedText?: string;
	url?: string;
	file?: File;
	requiredEvidence?: EvidenceRequirements;
	onSubmit?: (values: CaseEvidenceForm) => void;
	amend?: boolean;
	isDrawing?: boolean;
	handleSave?: (value: boolean) => void;
}) {
	const { closeModal } = useModalControl();
	const modal = useModal();
	const caseEvidenceMutation = useEvidenceMutation();
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [exhibitRequired, setRequiredExhibit] = useState<boolean>(false);
	const [descriptionRequired, setRequiredDescription] = useState<boolean>(false);
	const [witnessRequired, setRequiredWitness] = useState<boolean>(false);

	const form = useForm<CaseEvidenceForm>({
		resolver: zodResolver(caseEvidenceFormSchema),
		defaultValues: {
			id: data?.id || submarking?.id || "",
			case_id: requiredEvidence?.case_id || data?.case_id || submarking?.case_id || newSubmarking?.case_id,
			parent_id: data?.id || submarking?.parent_id || submarking?.id || newSubmarking?.id,
			case_record_user_id: data?.case_record_user_id || submarking?.case_record_user_id || newSubmarking?.case_record_user_id || "",
			evidence_type: requiredEvidence?.evidence_type || data?.evidence_type || submarking?.evidence_type || newSubmarking?.evidence_type,
			parties: requiredEvidence?.parties || data?.parties || submarking?.parties || newSubmarking?.parties,
			status: data?.status || submarking?.status || "",
			exhibit: data?.exhibit || submarking?.exhibit || undefined,
			witness_name: data?.witness_name || submarking?.witness_name,
			description: selectedText || data?.description || submarking?.description,
			purpose: data?.purpose || submarking?.purpose || "",
			evidence: file ? file : null,
			category: data?.category || [],
		},
	});

	const storedTime = sessionStorage.getItem("password_entered_at");

	const handleSubmit = async (values: CaseEvidenceForm) => {
		const expirationTime = moment(storedTime).add(5, "minutes");

		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			await caseEvidenceMutation.mutateAsync(values);
			closeModal();
		} else {
			try {
				modal.openModal({
					allowClose: true,
					title: "Confirm Submit",
					description: "Are you sure? This action cannot be undone.",
					size: "sm",
					showHeader: true,
					showFooter: false,
					component: (
						<ConfirmWithPasswordModal
							onSubmit={async () => {
								try {
									await caseEvidenceMutation.mutateAsync(values);
									closeModal();
								} catch (error) {
									console.log("error", error);
								}
							}}
						/>
					),
				});
			} catch (error) {
				console.log(error);
			}
		}
	};

	useEffect(() => {
		if (form.formState.isSubmitSuccessful && isSubmitting) {
			handleSave?.(true);
			closeModal();
		}
	}, [handleSave, setIsSubmitting, isSubmitting, form.formState.isSubmitSuccessful]);

	useEffect(() => {
		if (
			form.watch("description")?.startsWith(" ") ||
			form.watch("description") === "" ||
			form.watch("description") === " " ||
			form.watch("description") === null ||
			form.watch("description") === undefined
		) {
			setRequiredDescription(true);
		} else {
			setRequiredDescription(false);
		}
		if (
			form.watch("exhibit")?.startsWith(" ") ||
			form.watch("exhibit") === "" ||
			form.watch("exhibit") === " " ||
			form.watch("exhibit") === null ||
			form.watch("exhibit") === undefined
		) {
			setRequiredExhibit(true);
		} else {
			setRequiredExhibit(false);
		}
		if (
			form.watch("witness_name")?.startsWith(" ") ||
			form.watch("witness_name") === "" ||
			form.watch("witness_name") === " " ||
			form.watch("witness_name") === null ||
			form.watch("witness_name") === undefined
		) {
			setRequiredWitness(true);
		} else {
			setRequiredWitness(false);
		}
	}, [form.watch("exhibit"), form.watch("witness_name"), form.watch("description")]);

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(onSubmit ? onSubmit : handleSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4">
							<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
								{file && (
									<div className="w-full col-span-12 flex flex-row justify-between">
										<img src={url} alt={file?.name} />
									</div>
								)}
								{data?.evidence_type !== "Testimonial" && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="exhibit"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>Exhibit</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" required={exhibitRequired} {...field} />
													</FormGroup>
													{exhibitRequired && <FormMessage>Required</FormMessage>}{" "}
												</FormItem>
											)}
										/>
									</div>
								)}
								{data?.evidence_type === "Testimonial" && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="witness_name"
											render={({ field }) => (
												<FormItem>
													<FormLabel required>Witness Name</FormLabel>
													<FormGroup iconPosition="left">
														<Input autoComplete="off" required={witnessRequired} {...field} />
													</FormGroup>
													{witnessRequired && <FormMessage>Required</FormMessage>}
												</FormItem>
											)}
										/>
									</div>
								)}
								<div className="col-span-12 flex">
									<FormField
										control={form.control}
										name="description"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>Description</FormLabel>
												<FormGroup iconPosition="left">
													<TextareaAutosize
														autoComplete="off"
														required={descriptionRequired}
														maxRows={5}
														className="resize-none w-full h-full text-foreground flex px-3 py-2.5 text-base rounded-md border-border border focus:border-focus-border focus:ring-2 ring-offset-0 focus:ring-focus-ring/30 outline-0 outline-none"
														maxLength={1000}
														{...field}
													/>
												</FormGroup>
												{descriptionRequired && <FormMessage>Required</FormMessage>}
											</FormItem>
										)}
									/>
								</div>
								{data?.evidence_type !== "Testimonial" && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="purpose"
											render={({ field }) => (
												<FormItem>
													<FormLabel>Purpose</FormLabel>
													<FormGroup iconPosition="left">
														<TextareaAutosize
															autoComplete="off"
															maxRows={5}
															className="resize-none w-full h-full text-foreground flex px-3 py-2.5 text-base rounded-md border-border border focus:border-focus-border focus:ring-2 ring-offset-0 focus:ring-focus-ring/30 outline-0 outline-none"
															maxLength={1000}
															{...field}
														/>
													</FormGroup>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>
								)}
								{data?.evidence_type === "Testimonial" && amend && (
									<div className="col-span-12 flex">
										<FormField
											control={form.control}
											name="evidence"
											render={() => {
												return (
													<FormItem>
														<FormLabel required>Judicial Affidavit</FormLabel>
														<FormGroup iconPosition="left">
															<InputFile
																className="!relative"
																onChange={(e) => {
																	if (e.target?.files?.[0]) {
																		form.setValue("evidence", e.target?.files?.[0]);
																	}
																}}
															/>
														</FormGroup>
													</FormItem>
												);
											}}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
				{!isDrawing ? (
					<div className="flex flex-row justify-between w-full gap-4 mt-4">
						<Button variant="ghost" type="button" onClick={closeModal}>
							Cancel
						</Button>
						<Button
							type="submit"
							loading={form.formState.isSubmitting}
							onClick={() => closeModal()}
							disabled={(data?.evidence_type !== "Testimonial" ? exhibitRequired : witnessRequired) || descriptionRequired}
						>
							{data?.id || submarking?.id ? "Update" : "Submit"}
						</Button>
					</div>
				) : (
					<div className="flex flex-row items-center w-full gap-4 mt-4">
						<Button variant="ghost" type="button" onClick={closeModal}>
							Close
						</Button>

						<div className="flex flex-row gap-2 ml-auto">
							<Tooltip
								className="z-[90]"
								content={
									<div className="text-foreground-dark/90">
										Add the exhibit to the list. <br /> You can still keep editing the PDF.
									</div>
								}
								side="top"
							>
								<Button
									type="submit"
									variant="default"
									loading={form.formState.isSubmitting}
									onClick={() => closeModal()}
									disabled={(data?.evidence_type !== "Testimonial" ? exhibitRequired : witnessRequired) || descriptionRequired}
								>
									{submarking?.exhibit ? "Update" : "Add"}
								</Button>
							</Tooltip>
						</div>
					</div>
				)}
			</form>
		</Form>
	);
}
